import {
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  NumberInput,
  Stack,
  Text,
  Title,
  Table,
  SimpleGrid,
  Divider,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import _ from "lodash";
import ReactJson from "react-json-view";
import { useServerApi } from "../hooks/userServerApi";
import { useFormatter } from "../hooks/useFomatter";
import { useTranslation } from "react-i18next";

const skippedUplineText = (indexes) => {
  const ranges = [];
  let start = indexes[0];
  let previous = indexes[0];
  for (let index = 1; index <= indexes.length; index++) {
    const current = indexes[index];
    if (current === previous + 1) {
      previous = current;
      continue;
    }
    ranges.push(start === previous ? `Upline ${start}` : `Upline ${start}–${previous}`);
    start = current;
    previous = current;
  }
  return ranges.join(", ");
};

const ProducerCalculation = ({ calculation }) => {
  const { t } = useTranslation();
  if (!calculation?.steps?.length) return null;
  const title = calculation.levelName
    ? `${calculation.levelName} calculation`
    : "Producer calculation";

  return (
    <Box
      mt="xl"
      p="md"
      sx={(theme) => ({
        backgroundColor:
          theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
        borderRadius: theme.radius.md,
      })}
    >
      <Title order={5}>{t(title)}</Title>
      <Text size="xs" color="dimmed" mt={4} mb="sm">
        {t("Rounding parameters applied")}
      </Text>
      {calculation.parameters?.length > 0 && (
        <Group spacing="xl" mb="md" grow>
          {calculation.parameters.map((item) => (
            <Box key={item.label}>
              <Text size="xs" color="dimmed">
                {t(item.label)}
              </Text>
              <Text size="sm" weight={600}>
                {t(item.value)}
              </Text>
              {item.detail && (
                <Text size="xs" color="dimmed">
                  {item.detail}
                </Text>
              )}
            </Box>
          ))}
        </Group>
      )}
      <Stack spacing="md">
        {calculation.steps.map((step, index) => (
          <div key={`${step.label}-${index}`}>
            <Text size="sm" weight={600}>
              {index + 1}. {t(step.label)}
            </Text>
            <Text
              size="sm"
              mt={4}
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {step.formula}
            </Text>
            {step.note && (
              <Text size="sm" color="dimmed" mt={2}>
                {step.note}
              </Text>
            )}
          </div>
        ))}
      </Stack>
    </Box>
  );
};

const TestResultTable = ({ result, title = "Split Result" }) => {
  const [formatter] = useFormatter();
  const { t } = useTranslation();
  const calculations = result.levelCalculations || [];
  const defaultKey =
    calculations.find((row) => row.levelName === "Producer")?.key ??
    calculations[0]?.key ??
    null;
  const [selectedKey, setSelectedKey] = useState(defaultKey);
  useEffect(() => {
    setSelectedKey((current) =>
      calculations.some((row) => row.key === current) ? current : defaultKey
    );
  }, [result, defaultKey, calculations]);
  const selected =
    calculations.find((row) => row.key === selectedKey) ||
    calculations.find((row) => row.levelName === "Producer") ||
    calculations[0];
  const TableHeader = () => (
    <thead>
      <tr>
        <th>#</th>
        <th>{t("Level")}</th>
        <th>{t("Percentage")}</th>
        <th>{t("Amount")}</th>
        <th></th>
      </tr>
    </thead>
  );

  const TableRows = ({ result }) => {
    let rows = [...result.levelsCommission]
      .sort((a, b) => b.level.index - a.level.index)
      .map((p) => {
        return {
          key: String(p.level.index),
          index: p.level.index,
          level: p.levelName,
          isRemainLevel: Boolean(p.isRemainLevel || p.level?.isRemainLevel),
          percentage: p.level.percentage,
          amount: p.totalAmount,
          payeeLabel: p.payeeLabel,
          redirected: p.redirected,
        };
      });
    return (
      <tbody>
        {rows.map((r) => {
          const active = selected?.key === r.key;
          return (
            <tr
              key={r.key}
              onClick={() => setSelectedKey(r.key)}
              style={{
                cursor: "pointer",
                background: active ? "#e6fcf5" : undefined,
              }}
            >
              <td>{r.index}</td>
              <td>
                {r.level}
                {r.redirected && r.payeeLabel ? ` → ${r.payeeLabel}` : ""}
              </td>
              <td>{r.percentage}%</td>
              <td>{formatter.currency(r.amount)}</td>
              <td style={{ textAlign: "right", width: 1 }}>
                {r.isRemainLevel ? (
                  <Badge size="xs" variant="light">
                    {t("Remain")}
                  </Badge>
                ) : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    );
  };

  const TableFooter = ({ result }) => (
    <tfoot>
      <tr>
        <th></th>
        <th></th>
        <th></th>
        <th>
          {formatter.currency(
            result.levelsCommission.reduce((a, c) => a + c.totalAmount, 0)
          )}
        </th>
        <th></th>
      </tr>
    </tfoot>
  );
  return (
    <>
      {/* <ReactJson
        src={result}
        name="result"
        collapsed
        style={{ background: "white" }}
      /> */}
      <Title order={4} mb={4}>
        {t(title)}
      </Title>
      <Text size="xs" color="dimmed" mb="md">
        {t("Click a row to see its commission calculation")}
      </Text>
      {result.skippedUplines?.length > 0 && (
        <Text size="sm" mb="sm">
          {skippedUplineText(result.skippedUplines)}: {t("no overriding")}
        </Text>
      )}

      {/* <Group>
        <Text>
          Preimum Receivable : {formatter.currency(result.receiveable)}
        </Text>
      </Group> */}

      <Table>
        <TableHeader />
        <TableRows result={result} />
        <TableFooter result={result} />
      </Table>
      <ProducerCalculation calculation={selected || result.producerCalculation} />
    </>
  );
};

const OverridingTester = ({ form: mainForm, name }) => {
  const table = _.get(mainForm.values, name);
  const [preimum, setPreimum] = useState(5283);
  const upperCount = (table || []).filter((row) => row.levelType === "upper").length;
  const [uplineCount, setUplineCount] = useState(upperCount);
  const [uplineTouched, setUplineTouched] = useState(false);
  useEffect(() => {
    if (!uplineTouched) setUplineCount(upperCount);
  }, [upperCount, uplineTouched]);
  const [skipProviderRate, setSkipProviderRate] = useState(false);
  const [rate, setRate] = useState(65.25);
  const [hasDiscountRate, setHasDiscountRate] = useState(false);
  const [discountRate, setDiscountRate] = useState();
  const [internalExchangeRate, setInternalExchangeRate] = useState(7.82);
  const [providerExchangeRate, setProviderExchangeRate] = useState(7.84);
  const [api] = useServerApi();
  const [formatter] = useFormatter();
  const [testResult, setTestResult] = useState(null);
  const { t } = useTranslation();
  const discountTooHigh =
    hasDiscountRate &&
    discountRate !== undefined &&
    discountRate !== "" &&
    Number(discountRate) > Number(rate) + 1e-9;

  const genResult = async () => {
    let result = await api.getOverridingResult({
      preimum,
      rate,
      providerExchangeRate,
      internalExchangeRate,
      uplineCount,
      hasDiscountRate,
      discountRate: hasDiscountRate ? Number(discountRate) || 0 : 0,
      plan: mainForm.values,
    });
    // console.log(result.data);
    setTestResult(result.data);
  };

  // const handleKeyDown = () => {
  //   console.log("keyDown");
  // };
  return (
    <>
      {/* <ReactJson
        src={table}
        name="table"
        collapsed
        style={{ background: "white" }}
      /> */}
      <SimpleGrid cols={2}>
        <NumberInput
          label={t("Premium ($)")}
          defaultValue={preimum}
          precision={2}
          parser={formatter.currencyParser}
          formatter={formatter.currencyFormat}
          onChange={(v) => setPreimum(v)}
        />
        <NumberInput
          label={t("Provider Rate (%)")}
          precision={2}
          defaultValue={rate}
          placeholder={"50"}
          onChange={(v) => setRate(v)}
        />
      </SimpleGrid>
      <Group align="center" spacing="sm" mt="md">
        <Text size="sm" weight={500}>
          {t("Discount Rate")}
        </Text>
        <Checkbox
          aria-label={t("Have discount rate")}
          checked={hasDiscountRate}
          onChange={(event) => setHasDiscountRate(event.currentTarget.checked)}
        />
        <NumberInput
          aria-label={t("Discount Rate")}
          precision={4}
          min={0}
          disabled={!hasDiscountRate}
          value={discountRate}
          placeholder="0.0000"
          onChange={(value) => setDiscountRate(value)}
          sx={{ width: 180 }}
        />
      </Group>
      {discountTooHigh && (
        <Text size="sm" color="red" mt={4}>
          {t(
            "A discount rate cannot be higher than that year's gross rate."
          )}
        </Text>
      )}
      <SimpleGrid cols={2} mt="md">
        <NumberInput
          label={t("Provider Exchange Rate")}
          precision={5}
          defaultValue={providerExchangeRate}
          placeholder={"50"}
          onChange={(v) => setProviderExchangeRate(v)}
        />
        <NumberInput
          label={t("Internal Exchange Rate")}
          precision={5}
          defaultValue={internalExchangeRate}
          placeholder={"50"}
          onChange={(v) => setInternalExchangeRate(v)}
        />
        <NumberInput
          label={t("Number of Producer's upline")}
          min={0}
          value={uplineCount}
          onChange={(value) => {
            setUplineTouched(true);
            setUplineCount(value);
          }}
        />
      </SimpleGrid>
      {testResult?.skippedUplines?.length > 0 && (
        <Text size="sm" mt="sm">
          {skippedUplineText(testResult.skippedUplines)}: {t("no overriding")}
        </Text>
      )}

      <Button
        size="xs"
        mt={"xl"}
        mb={50}
        disabled={!preimum || !rate || discountTooHigh}
        onClick={genResult}
      >
        {t("Test")}
      </Button>

      {testResult && (
        <>
          <Divider mb={"xl"} />
          <TestResultTable result={testResult} />
        </>
      )}
    </>
  );
};

export default OverridingTester;
